import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession, portalApi, type CaptchaData } from '@/api/portal';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [merchantNo, setMerchantNo] = useState('');
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const data = await portalApi.captcha();
      setCaptcha(data);
      setVerifyCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码加载失败');
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    if (getSession()) {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadCaptcha();
  }, [navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!merchantNo.trim() || !password.trim()) {
      setError('请输入商户号和密码');
      return;
    }
    if (!captcha?.key || !verifyCode.trim()) {
      setError('请输入验证码');
      return;
    }
    setLoading(true);
    try {
      await signIn(merchantNo.trim(), password.trim(), captcha.key, verifyCode.trim());
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败，请检查商户号、密码和验证码');
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-600 tech-glow-blue">
            <i className="ri-bank-card-line text-white text-lg" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-100">FaPay支付商户端</h1>
            <p className="text-xs text-slate-500">配置、查账、接入文档</p>
          </div>
        </div>

        <div className="tech-card p-6 md:p-8">
          <h2 className="text-lg font-semibold text-slate-100 mb-1">商户登录</h2>
          <p className="text-sm text-slate-400 mb-6">请输入商户号、密码和验证码。</p>

          {error && (
            <div className="mb-4 rounded-lg bg-red-500/10 px-3 py-2.5 text-sm text-red-400 border border-red-500/20">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">商户号</label>
              <input
                value={merchantNo}
                onChange={(e) => setMerchantNo(e.target.value)}
                placeholder="请输入商户号"
                className="tech-input w-full px-3 py-2.5 text-sm"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">密码</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                className="tech-input w-full px-3 py-2.5 text-sm"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">验证码</label>
              <div className="flex items-center gap-3">
                <input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder="输入算式结果"
                  className="tech-input flex-1 px-3 py-2.5 text-sm"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={loadCaptcha}
                  disabled={captchaLoading}
                  className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-700 bg-slate-900/80 text-xs text-slate-500 disabled:opacity-60"
                  style={{
                    width: 126,
                    height: 42,
                    minWidth: 126,
                    maxWidth: 126,
                    minHeight: 42,
                    maxHeight: 42,
                    padding: 0,
                    lineHeight: '42px',
                  }}
                  title="点击刷新算式验证码"
                >
                  {captchaLoading ? (
                    '加载中...'
                  ) : captcha?.img ? (
                    <img
                      src={captcha.img}
                      alt="验证码"
                      className="block"
                      width={126}
                      height={42}
                      style={{
                        width: 126,
                        height: 42,
                        minWidth: 126,
                        maxWidth: 126,
                        minHeight: 42,
                        maxHeight: 42,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  ) : (
                    '刷新'
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="tech-btn-primary w-full px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
